import {
  Divider,
  NetworkProfileCard,
  FilterCard,
  InfoBox,
  PayoutsCard,
  PayoutsOverTimeCard,
} from '@/components';
import { VisibleFilterKey } from '@/components/cards/FilterCard/Filters';
import { useNavigate, useParams } from 'react-router-dom';
import { getCurrentYear } from '@/utils/dateUtils';
import { useEffect, useMemo } from 'react';
import { Heading } from '@acast-tech/decibel';
import { QuarterlyTotals } from './QuarterlyTotals';
import YearTabs from './YearTabs';
import { useDerivedNetworkContext } from '@/context';
import { usePayoutDatesV2Query } from '@/services/revenueApi';
import { cardFullWidth } from '@/utils/styleUtils';
import { NoPayoutYetCard } from './NoPayoutYetCard';
import { YearString } from '@/types/state/filter';

const PayoutsPage = () => {
  const { year } = useParams();
  const navigate = useNavigate();
  const { selectedNetwork } = useDerivedNetworkContext();
  const {
    data: payoutsData,
    isPending: isPayoutsDataPending,
    isError: isPayoutsDataError,
  } = usePayoutDatesV2Query(selectedNetwork.id ?? '');

  const payoutYears = useMemo(() => payoutsData?.payout_years ?? [], [payoutsData]);
  const selectedYear = (year ?? getCurrentYear()) as YearString;

  useEffect(() => {
    if (payoutYears.length > 0 && (!year || !payoutYears.includes(year))) {
      navigate(`/${selectedNetwork.id}/payouts/${payoutYears[0]}`);
    }
  }, [year, navigate, selectedNetwork.id, payoutYears]);

  const networkHasNotReceivedPayouts = useMemo(() => {
    return !isPayoutsDataPending && payoutYears?.length === 0 && !isPayoutsDataError;
  }, [isPayoutsDataPending, payoutYears, isPayoutsDataError]);

  const visibleFilters: VisibleFilterKey[] = [VisibleFilterKey.Networks];

  return (
    <>
      <div className="pt-6 pb-10">
        <Heading variant="h1" mb={0}>
          Payouts
        </Heading>
      </div>

      <FilterCard visibleFilters={visibleFilters} />

      <section className="grid grid-cols-12 grid-rows-auto gap-6 mb-6">
        <NetworkProfileCard visibleFilters={visibleFilters} className={cardFullWidth} />
      </section>

      <div className="flex flex-col gap-10">
        <h2 className="text-textDarkGrey text-[34px] font-[700] font-telegraf col-span-12">
          Payout statements over time
        </h2>

        {networkHasNotReceivedPayouts ? (
          <NoPayoutYetCard />
        ) : (
          <>
            <PayoutsOverTimeCard />

            {payoutsData && (
              <div className="overflow-x-auto">
                <YearTabs
                  selectedYear={selectedYear}
                  selectedNetwork={selectedNetwork}
                  isPending={isPayoutsDataPending}
                  years={payoutYears}
                />
                <Divider />
              </div>
            )}

            <div>
              <QuarterlyTotals year={selectedYear} />
            </div>

            <PayoutsCard year={selectedYear} />
          </>
        )}

        <InfoBox
          title=""
          intent="secondary"
          link={{
            to: 'https://learn.acast.com/en/articles/5146617-how-and-when-will-i-get-paid',
            text: 'Read more',
          }}
        >
          <div className="flex flex-col gap-4 mb-3">
            <div>
              <h5 className="font-bold mb-1">What is included in statements?</h5>
              <p>
                They contain all earnings that are ready to be paid that month. As some campaigns
                take longer to verify (e.g. programmatic ads), statements do not contain all
                earnings from the previous month, but can include earnings from several different
                periods (e.g. programmatic earnings from 2 months before).
              </p>
            </div>
            <div>
              <h5 className="font-bold mb-1">When are statements sent?</h5>
              <p>
                Payout statements are sent by email around the 15th of each month. If the amount
                ready for payout is less than $100, you will not receive a statement or payout that
                month, and your cumulative earnings will appear on a future statement.
              </p>
            </div>
            <div>
              <h5 className="font-bold mb-1">When are statements paid?</h5>
              <p>
                If you’re using our automatic (‘self-billing’) process, you will receive payment
                approximately 60 days after receiving the self-bill statement from us. If you’re
                using our invoicing process, you will receive payment approximately 60 days after we
                receive your invoice.
              </p>
            </div>
          </div>
        </InfoBox>
      </div>
    </>
  );
};

export default PayoutsPage;
